/**
 * Cell Allocation — the warning at the moment a proposal is accepted.
 *
 * Owner rulings on roadmap item C-2 (Oct 14): a proposal counts only once it is
 * accepted, and the warning at accept time reads "if you accept this". It waits
 * until the cycle owner has opened the cycle's Allocation tab once, so nobody is
 * warned about shares the owner has not yet looked at.
 *
 * Reads get_cell_allocation_consumption twice (migration 972): as the cycle
 * stands, and as it would stand with this proposal's list committed. Shows the
 * bands the acceptance would leave over or under their share. A notice, never a
 * block: accepting stays the decision-maker's call. Renders nothing when there is
 * nothing to say, and nothing when the read fails.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Callout } from '@/components/ds';
import { bandIfAcceptedSentence, bandsOffIfAccepted, type BandIfAccepted } from '@/lib/cellAllocation';
import { fetchCellAllocationConsumption } from '@/lib/cellAllocationService';

interface Props {
  cycleId: string;
  proposalId: string;
}

export function AllocationIfAccepted({ cycleId, proposalId }: Props) {
  const [bands, setBands] = useState<BandIfAccepted[]>([]);

  useEffect(() => {
    let cancelled = false;
    setBands([]);
    Promise.all([
      fetchCellAllocationConsumption(cycleId),
      fetchCellAllocationConsumption(cycleId, { ifAcceptedProposalId: proposalId }),
    ])
      .then(([now, after]) => {
        if (cancelled) return;
        if (!now.firstOpenedAt || now.bands.length === 0) return;
        setBands(bandsOffIfAccepted(now.bands, after.bands));
      })
      .catch((e) => {
        console.warn('[AllocationIfAccepted] could not read the allocation:', e);
      });
    return () => { cancelled = true; };
  }, [cycleId, proposalId]);

  if (bands.length === 0) return null;

  return (
    <Callout
      tone="mustard"
      label="Allocation"
      title="If you accept this"
      action={
        <Link
          to={`/prioritization/cycles/${cycleId}?tab=allocation`}
          className="whitespace-nowrap text-xs font-medium text-[var(--text-sec)] underline underline-offset-2 hover:text-[var(--text-pri)]"
        >
          See the shares
        </Link>
      }
    >
      <ul className="space-y-1 text-sm" data-testid="allocation-if-accepted">
        {bands.map((b) => (
          <li key={b.name}>{bandIfAcceptedSentence(b)}</li>
        ))}
      </ul>
    </Callout>
  );
}

export default AllocationIfAccepted;
